import { IMAGES, buildWhatsAppUrl } from "@/lib/site";
import { Sofa, Users, ArrowUpRight, Coffee, Flame } from "lucide-react";

const sitouts = [
  {
    id: "family-couple",
    name: "Family & Couple Sitout",
    tag: "Private • Peaceful • Morning Coffee",
    image: IMAGES.sitoutFamily,
    icon: Sofa,
    description:
      "A private open-air sitout surrounded by tropical greenery — the perfect spot to sip your morning filter coffee, journal, or share quiet moments as a couple. Handcrafted Athangudi-style tile flooring and a cool blue trellis roof make it feel like your own garden pavilion.",
    features: [
      { icon: Coffee, label: "Perfect for morning coffee" },
      { icon: Sofa, label: "Private garden view" },
      { icon: Users, label: "Ideal for couples & families" },
    ],
  },
  {
    id: "group",
    name: "Group & Friends Sitout",
    tag: "Bonfire • Music • Late Night Chats",
    image: IMAGES.sitoutGroup,
    icon: Flame,
    description:
      "A spacious open-air deck built around a central bonfire pit — designed for groups, offsites and long family evenings. Surrounded by lush plants, string lights and comfortable seating for 12+ guests, this is where the best stories are told.",
    features: [
      { icon: Flame, label: "Central bonfire pit" },
      { icon: Users, label: "Seats 12+ guests" },
      { icon: Sofa, label: "Perfect for groups & offsites" },
    ],
  },
];

const Sitouts = () => {
  return (
    <section
      id="sitouts"
      data-testid="sitouts-section"
      className="relative py-24 md:py-32 bg-[#F8FAF7] overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
        <div className="max-w-2xl reveal">
          <div className="flex items-center gap-3 mb-5">
            <span className="h-px w-8 bg-[#D4AF37]" />
            <span className="text-[11px] tracking-[0.3em] uppercase text-[#D4AF37] font-medium">
              Our Sitouts
            </span>
          </div>
          <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl text-[#1B4332] leading-[1.05] tracking-tight">
            Two beautiful{" "}
            <span className="italic font-light">outdoor lounges</span>
          </h2>
          <p className="mt-5 text-[#1A1A1A]/75 text-base md:text-lg leading-relaxed">
            One of the most-loved corners of Navilukallu — we've built two
            distinct sitouts so every kind of guest can find their perfect
            spot to slow down.
          </p>
        </div>

        <div className="mt-14 grid lg:grid-cols-2 gap-8">
          {sitouts.map((s, i) => (
            <article
              key={s.id}
              className="reveal group rounded-3xl overflow-hidden bg-white border border-[#1B4332]/10 shadow-sm hover:-translate-y-1 hover:shadow-2xl transition-all duration-500"
              style={{ animationDelay: `${i * 0.12}s` }}
              data-testid={`sitout-card-${s.id}`}
            >
              <div className="relative aspect-[4/3] overflow-hidden bg-[#1B4332]">
                <img
                  src={s.image}
                  alt={s.name}
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-cover group-hover:scale-[1.05] transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#1A1A1A]/70 via-transparent to-transparent" />
                <div className="absolute top-4 left-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/90 backdrop-blur text-[#1B4332] text-[10px] tracking-[0.25em] uppercase font-medium">
                  <s.icon size={13} strokeWidth={1.8} />
                  {s.tag}
                </div>
              </div>

              <div className="p-7 md:p-9">
                <h3 className="font-heading text-2xl md:text-3xl text-[#1B4332] leading-tight">
                  {s.name}
                </h3>
                <p className="mt-3 text-[#1A1A1A]/70 leading-relaxed text-sm md:text-base">
                  {s.description}
                </p>

                <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-2">
                  {s.features.map((f) => (
                    <div
                      key={f.label}
                      className="flex items-center gap-2 py-2 px-3 rounded-lg bg-[#1B4332]/5 border border-[#1B4332]/10"
                    >
                      <f.icon size={14} strokeWidth={1.8} className="text-[#D4AF37] shrink-0" />
                      <span className="text-xs text-[#1A1A1A]/80">{f.label}</span>
                    </div>
                  ))}
                </div>

                <a
                  href={buildWhatsAppUrl(
                    `Hi Navilukallu Homestay, I'd like to enquire about the *${s.name}*. Please share more details.`
                  )}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`sitout-enquire-${s.id}`}
                  className="mt-7 inline-flex items-center gap-2 px-5 py-3 rounded-full bg-[#1B4332] text-white text-sm hover:bg-[#D4AF37] hover:text-[#1A1A1A] transition-colors"
                >
                  Enquire on WhatsApp
                  <ArrowUpRight size={16} />
                </a>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Sitouts;
